import { Button, Checkbox, DatePicker, Typography } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { sortBy } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CustomFilterContainer,
  CustomFilterPopover,
  FilterActions,
  SecondaryFilterLabel,
} from 'components/data/helpers/filters.style';
import { type CustomFilterDropdownProps } from 'horizon/types/TableColumnDef';
import { useGetInspectionTypesQuery } from 'types/atlas-graphql';
import { DATE_FORMAT } from 'utils/constants';
import { resolveRelativeDateToken } from 'utils/relativeDateTokens';

import { InspectionTypeList } from './LastInspectedFilter.style';
import {
  type LastInspectedFilterState,
  buildLastInspectedSelectedKeys,
  getLastInspectedFilterState,
  LAST_INSPECTED_QUICK_PICKS,
} from './utils';

type Props = CustomFilterDropdownProps;

export const LastInspectedFilter = ({
  selectedKeys,
  setSelectedKeys,
  confirm,
  clearFilters,
}: Props) => {
  const { t } = useTranslation();
  const { data, loading, error } = useGetInspectionTypesQuery();

  const inspectionTypes = useMemo(
    () => sortBy(data?.inspectionTypes ?? [], ({ name }) => name.toLowerCase()),
    [data?.inspectionTypes]
  );
  const { inspectionTypeIds, notInspectedSince } = getLastInspectedFilterState(selectedKeys);

  const today = dayjs().startOf('day');
  const presets = LAST_INSPECTED_QUICK_PICKS.map(({ token, unit, amount }) => ({
    token,
    label:
      unit === 'month'
        ? t('assets.lastInspectedFilter.monthsAgo', { count: amount })
        : t('assets.lastInspectedFilter.yearsAgo', { count: amount }),
    value: dayjs(String(resolveRelativeDateToken(token, 'past', today))),
  }));
  const dateValue = notInspectedSince
    ? dayjs(String(resolveRelativeDateToken(notInspectedSince, 'past', today)))
    : null;

  const handleUpdate = (state: LastInspectedFilterState) => {
    setSelectedKeys(buildLastInspectedSelectedKeys(state));
  };

  const handleDateChange = (date: Dayjs | null) => {
    // A quick pick is stored as its relative token, so a saved view keeps meaning
    // "more than 3 months ago" instead of freezing the date it was saved on.
    const preset = date ? presets.find(({ value }) => value.isSame(date, 'day')) : undefined;
    handleUpdate({
      inspectionTypeIds,
      notInspectedSince: date ? preset?.token ?? date.format(DATE_FORMAT) : undefined,
    });
  };

  const handleInspectionTypesChange = (typeIds: string[]) => {
    handleUpdate({ inspectionTypeIds: typeIds, notInspectedSince });
  };

  const handleConfirm = () => {
    confirm({ closeDropdown: true });
  };

  // Like the other filters, Reset applies at once: antd only applies a custom filter on confirm
  const handleReset = () => {
    clearFilters?.();
    setSelectedKeys([]);
    confirm({ closeDropdown: true });
  };

  return (
    <CustomFilterPopover
      className="ant-table-filter-dropdown"
      $width="20rem"
      data-testid="last-inspected-filter"
    >
      <CustomFilterContainer>
        <SecondaryFilterLabel id="last-inspected-filter-not-inspected-since">
          {t('assets.lastInspectedFilter.notInspectedSince', 'Not Inspected Since')}
        </SecondaryFilterLabel>
        <DatePicker
          aria-labelledby="last-inspected-filter-not-inspected-since"
          data-testid="last-inspected-filter-date"
          format={DATE_FORMAT}
          placeholder={t('assets.lastInspectedFilter.selectDate', 'Select date')}
          presets={presets.map(({ label, value }) => ({ label, value }))}
          value={dateValue}
          onChange={handleDateChange}
          disabledDate={date => date.isAfter(today, 'day')}
          getPopupContainer={trigger => trigger.parentElement ?? document.body}
          style={{ width: '100%' }}
        />
      </CustomFilterContainer>

      <CustomFilterContainer>
        <SecondaryFilterLabel>
          {t('assets.lastInspectedFilter.inspectionTypes', 'Inspection Types')}
        </SecondaryFilterLabel>
        <InspectionTypeList>
          {loading && !inspectionTypes.length ? (
            <Typography.Text type="secondary">
              {t(
                'assets.lastInspectedFilter.loadingInspectionTypes',
                'Loading Inspection types...'
              )}
            </Typography.Text>
          ) : null}
          {error && !inspectionTypes.length ? (
            <Typography.Text type="danger">
              {t(
                'assets.lastInspectedFilter.inspectionTypesLoadError',
                'Unable to load Inspection types: {{message}}',
                { message: error.message }
              )}
            </Typography.Text>
          ) : null}
          <Checkbox.Group
            value={inspectionTypeIds ?? []}
            onChange={values => handleInspectionTypesChange(values as string[])}
            options={inspectionTypes.map(({ id, name }) => ({
              label: name,
              value: id,
              disabled: loading,
            }))}
          />
        </InspectionTypeList>
      </CustomFilterContainer>

      <FilterActions>
        <Button type="link" size="small" onClick={handleReset}>
          {t('table.reset', 'Reset')}
        </Button>
        <Button type="primary" size="small" onClick={handleConfirm}>
          {t('table.ok', 'OK')}
        </Button>
      </FilterActions>
    </CustomFilterPopover>
  );
};
